import React, { useState, useEffect } from 'react';
import { apiService } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Users, CalendarCheck, CalendarX, TrendingUp, Bed, Edit, Clock } from 'lucide-react';
import { formatCurrency, formatDateShort, formatDateRange } from '@/utils/format';

/**
 * DashboardOverview - Shows real-time statistics
 * 
 * UPDATED: Now uses apiService for authenticated requests
 */
function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiService.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-gray-500">
        Failed to load statistics
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Dashboard Overview
        </h2>
        <p className="text-sm sm:text-base text-gray-600">Today's snapshot and recent activity</p>
      </div>

      {/* Today's Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Check-ins</CardTitle>
            <CalendarCheck className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-green-600">{stats.today.check_ins}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Check-outs</CardTitle>
            <CalendarX className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-orange-600">{stats.today.check_outs}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Guests</CardTitle>
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600">{stats.occupancy.current_guests}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Occupancy</CardTitle>
            <Bed className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-purple-600">{stats.occupancy.occupancy_rate}%</div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              {stats.occupancy.occupied_rooms}/{stats.occupancy.total_rooms} rooms
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Check-ins & Check-outs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CalendarCheck className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              Today's Check-ins
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {stats.today.check_ins_list.length === 0 ? (
              <p className="text-gray-500 text-center py-4 text-sm sm:text-base">No check-ins today</p>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {stats.today.check_ins_list.map((booking) => (
                  <div key={booking.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-sm sm:text-base text-gray-900 flex items-center gap-2">
                      {booking.guest_name}
                      {booking.car_id && <span title="Car rental" className="text-blue-600">🚗</span>}
                      {booking.transfer_required && <span title="Airport transfer" className="text-green-600">✈️</span>}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">{booking.guest_email}</div>
                    <div className="text-xs sm:text-sm text-gray-500 mt-1">
                      {booking.num_guests} guest{booking.num_guests !== 1 ? 's' : ''} · {formatCurrency(booking.total_amount)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CalendarX className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
              Today's Check-outs
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {stats.today.check_outs_list.length === 0 ? (
              <p className="text-gray-500 text-center py-4 text-sm sm:text-base">No check-outs today</p>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {stats.today.check_outs_list.map((booking) => (
                  <div key={booking.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-sm sm:text-base text-gray-900 flex items-center gap-2">
                      {booking.guest_name}
                      {booking.car_id && <span title="Car rental" className="text-blue-600">🚗</span>}
                      {booking.transfer_required && <span title="Airport transfer" className="text-green-600">✈️</span>}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">{booking.guest_email}</div>
                    <div className="text-xs sm:text-sm text-gray-500 mt-1">
                      {booking.num_guests} guest{booking.num_guests !== 1 ? 's' : ''} · {formatCurrency(booking.total_amount)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Guest Modifications */}
      {stats.recent_modifications && stats.recent_modifications.modifications.length > 0 && (
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Edit className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
              Recent Guest Modifications
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="space-y-3">
              {stats.recent_modifications.modifications.map((mod, index) => (
                <div key={`${mod.booking_id}-${index}`} className="p-3 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium text-sm sm:text-base text-gray-900">
                      {mod.guest_name}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatTimeAgo(mod.modified_at)}
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 mb-1">
                    {mod.guest_email} • Booking #{mod.booking_id.slice(0, 8)}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 mb-2">
                    {formatDateRange(mod.check_in, mod.check_out)} • {formatCurrency(mod.total_amount)}
                  </div>
                  <div className="space-y-1">
                    {mod.changes_summary.map((change, changeIndex) => (
                      <div key={changeIndex} className="text-xs text-orange-800 bg-orange-100 px-2 py-1 rounded">
                        {change}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {stats.recent_modifications.count > 5 && (
                <p className="text-xs text-gray-500 text-center pt-2">
                  Showing 5 of {stats.recent_modifications.count} recent modifications
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming & Recent Bookings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <CardHeader className="p-3 sm:p-4">
            <CardTitle className="flex items-center justify-between text-base sm:text-lg">
              <span className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                Upcoming (Next 7 Days)
              </span>
              <span className="text-lg sm:text-xl font-bold text-blue-600">{stats.upcoming.count}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            {stats.upcoming.count === 0 ? (
              <p className="text-gray-500 text-center py-4 text-sm">No upcoming bookings</p>
            ) : (
              <div className="space-y-1.5 max-h-96 overflow-y-auto pr-2">
                {stats.upcoming.bookings.map((booking) => (
                  <div key={booking.id} className="p-2 bg-gray-50 rounded border border-gray-100 hover:border-blue-200 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-xs sm:text-sm text-gray-900 truncate flex items-center gap-1.5">
                        {booking.guest_name}
                        {booking.car_id && <span title="Car rental" className="text-xs">🚗</span>}
                        {booking.transfer_required && <span title="Airport transfer" className="text-xs">✈️</span>}
                      </div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        {formatDateShort(booking.check_in)}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {formatDateRange(booking.check_in, booking.check_out)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-3 sm:p-4">
            <CardTitle className="flex items-center justify-between text-base sm:text-lg">
              <span>Recent (Last 7 Days)</span>
              <span className="text-lg sm:text-xl font-bold text-green-600">{stats.recent.count}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            {stats.recent.count === 0 ? (
              <p className="text-gray-500 text-center py-4 text-sm">No recent bookings</p>
            ) : (
              <div className="space-y-1.5 max-h-96 overflow-y-auto pr-2">
                {stats.recent.bookings.map((booking) => (
                  <div key={booking.id} className="p-2 bg-gray-50 rounded border border-gray-100 hover:border-green-200 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-xs sm:text-sm text-gray-900 truncate flex items-center gap-1.5">
                        {booking.guest_name}
                        {booking.car_id && <span title="Car rental" className="text-xs">🚗</span>}
                        {booking.transfer_required && <span title="Airport transfer" className="text-xs">✈️</span>}
                      </div>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
                        booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {booking.payment_status === 'paid' ? '✓' : '⏳'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {formatDateRange(booking.check_in, booking.check_out)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default DashboardOverview;
